import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import GoogleAsset from '../../assets/icons8-google 1.png';
import AzureAsset from '../../assets/Frame 59.png';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/New_petrotago.jfif';
import { useGoogleLogin } from '@react-oauth/google';
import { useMsal } from '@azure/msal-react';
import { EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { loginRequest } from '../../config/msalConfig';
import { requestLoginCode, verifyLoginCode, resendCode, loginWithGoogle, loginWithAzure, ApiError, LoginResult, MfaVerifyError } from '../../utils/auth';
import { useCountdown } from '../../hooks/useCountdown';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';

/* ── Isolated component so useGoogleLogin only mounts when a client ID exists ── */
const GoogleSignInButton: React.FC<{
  disabled: boolean;
  onSuccess: (accessToken: string) => void;
  onError:   () => void;
}> = ({ disabled, onSuccess, onError }) => {
  const signIn = useGoogleLogin({ onSuccess: r => onSuccess(r.access_token), onError });
  return (
    <Button
      variant="outlined"
      onClick={() => signIn()}
      disabled={disabled}
      startIcon={
        <Box component="img"
          src={require('../../assets/icons8-google 1.png')}
          alt="Google"
          sx={{ width: 18, height: 18 }}
        />
      }
      sx={{
        height: '43px', borderColor: '#E5E7EB', color: '#374151',
        borderRadius: '10px', textTransform: 'none', fontWeight: 500,
        fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
        '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
      }}
    >
      Sign in with Google
    </Button>
  );
};

interface LoginPageProps {
  onLogin: (user: UserData) => void;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'Admin' | 'Manager' | 'Operator' | 'Auditor';
  avatar?: string;
  permissions: string[];
  isOwner: boolean;
  sites: string[];
}

const VALID_ROLES = ['owner', 'Admin', 'Manager', 'Operator', 'Auditor'] as const;
type Role = typeof VALID_ROLES[number];

function toRole(raw: unknown): Role {
  if (typeof raw === 'string' && (VALID_ROLES as readonly string[]).includes(raw)) return raw as Role;
  return 'Operator';
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({
    open: false, message: '', severity: 'error',
  });
  const showToast = (message: string, severity: 'error' | 'success' = 'error') =>
    setToast({ open: true, message, severity });
  const closeToast = () => setToast(prev => ({ ...prev, open: false }));

  useEffect(() => {
    if (sessionStorage.getItem('pt360_session_expired')) {
      sessionStorage.removeItem('pt360_session_expired');
      showToast('Your session expired. Please log in again.');
    }
  }, []);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  // Step 1 of MFA login — checks the password and, if valid, emails a one-time code.
  // pendingToken lives only in memory (component state), never in storage (guide §3.1).
  const [mfaPending, setMfaPending] = useState<{ pendingToken: string; email: string } | null>(null);
  const [mfaCode,    setMfaCode]    = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  // Sign-in is throttled to 10 requests a minute; resend to one code per 60 s.
  const loginWait  = useCountdown();
  const resendWait = useCountdown();

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      showToast('Please enter both email and password');
      return;
    }
    if (loginWait.remaining > 0) return;
    setLoading(true);

    try {
      const { pendingToken, email, attemptsRemaining, trialsRemaining } = await requestLoginCode(formData.email, formData.password);
      setMfaPending({ pendingToken, email });
      setMfaCode('');
      setAttemptsLeft(attemptsRemaining ?? trialsRemaining ?? null);
      resendWait.start(60);   // a code was just sent; another may follow after 60 s
      showToast(`We emailed a login code to ${email}.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 429) {
        loginWait.start(apiErr.retryAfter ?? 60);
        showToast(apiErr.message);
      } else if (apiErr?.status != null) {
        showToast(apiErr.message);
      } else {
        showToast('Unable to reach the server. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  // Step 2 — the emailed code, paired with the pendingToken from step 1, issues tokens.
  const handleVerifyCode = async () => {
    if (!mfaPending || !mfaCode.trim()) { showToast('Enter the code we emailed you.'); return; }
    setMfaLoading(true);
    try {
      const { user }: LoginResult = await verifyLoginCode(mfaPending.pendingToken, mfaCode.trim());
      setMfaPending(null);
      setMfaCode('');
      onLogin({
        id:          user.id,
        name:        user.username || user.name || user.email,
        email:       user.email,
        role:        toRole(user.role),
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        isOwner:     user.isOwner === true,
        sites:       Array.isArray(user.assignedSites) && user.assignedSites.length > 0
                       ? user.assignedSites
                       : ['all'],
      });
    } catch (err) {
      const apiErr = err as MfaVerifyError;
      const left = apiErr?.attemptsRemaining ?? apiErr?.trialsRemaining;
      if (left != null) setAttemptsLeft(left);
      if (apiErr?.pendingTokenInvalid) {
        // 401: the code session expired or is gone — sign-in restarts from the password.
        setMfaPending(null); setMfaCode(''); setAttemptsLeft(null);
        showToast('Your sign-in code has expired. Please enter your password again.');
      } else {
        if (apiErr?.status === 429) loginWait.start(apiErr.retryAfter ?? 60);
        showToast(left != null ? `${apiErr?.message ?? 'Invalid code.'} ${left} attempt${left === 1 ? '' : 's'} left.` : (apiErr?.message ?? 'Invalid or expired code.'));
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const [resendLoading, setResendLoading] = useState(false);

  const handleResendCode = async () => {
    if (!mfaPending || resendLoading || resendWait.remaining > 0) return;
    setResendLoading(true);
    try {
      await resendCode(mfaPending.email, 'login');
      resendWait.start(60);
      showToast(`Sent a new code to ${mfaPending.email}.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      // 429 inside the 60-second window: count down instead of letting them click again.
      if (apiErr?.status === 429) resendWait.start(apiErr.retryAfter ?? 60);
      showToast(apiErr?.message ?? 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleMfaCancel = () => {
    setMfaPending(null);
    setMfaCode('');
    setAttemptsLeft(null);
  };

  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true);
    try {
      // Fetch user profile from Google using the access token
      const profileRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!profileRes.ok) throw new Error('Failed to retrieve Google profile.');
      const profile = await profileRes.json();

      const { user }: LoginResult = await loginWithGoogle({
        email:   profile.email,
        name:    profile.name,
        picture: profile.picture,
      });

      onLogin({
        id:          user.id,
        name:        user.username || user.name || user.email,
        email:       user.email,
        role:        toRole(user.role),
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        isOwner:     user.isOwner === true,
        sites:       Array.isArray(user.assignedSites) && user.assignedSites.length > 0
                       ? user.assignedSites
                       : ['all'],
      });
    } catch (err) {
      const apiErr = err as ApiError;
      showToast(apiErr?.message ?? 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Microsoft Azure SSO — redirect flow ── */
  // loginPopup() fails on MSAL v5 because the popup is a separate window with
  // empty sessionStorage — it can't find the PKCE code verifier the parent wrote.
  // loginRedirect() keeps everything in the same tab, so sessionStorage survives.
  const { instance } = useMsal();

  const [azurePending, setAzurePending] = useState<{ username: string; name: string } | null>(null);
  const [azureEmail,   setAzureEmail]   = useState('');
  const [azureLoading, setAzureLoading] = useState(false);

  // After loginRedirect() brings the user back, MsalProvider processes the code
  // exchange and fires LOGIN_SUCCESS. We detect that here and open the email dialog.
  useEffect(() => {
    const isPending = localStorage.getItem('azure_redirect_in_progress') === 'true';
    if (!isPending) return;

    // If MSAL already processed the redirect before this component mounted, accounts
    // are immediately available.
    const existing = instance.getAllAccounts();
    if (existing.length > 0) {
      localStorage.removeItem('azure_redirect_in_progress');
      const acct = existing[0];
      setAzureEmail(acct.username ?? '');
      setAzurePending({ username: acct.username ?? '', name: acct.name ?? acct.username ?? '' });
      return;
    }

    // Otherwise wait for MsalProvider to finish the code exchange.
    const cbId = instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const result = event.payload as AuthenticationResult;
        if (result?.account) {
          localStorage.removeItem('azure_redirect_in_progress');
          setAzureEmail(result.account.username ?? '');
          setAzurePending({ username: result.account.username ?? '', name: result.account.name ?? result.account.username ?? '' });
        }
      }
      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        localStorage.removeItem('azure_redirect_in_progress');
        showToast((event.error as any)?.message ?? 'Microsoft sign-in failed. Please try again.');
      }
    });

    return () => { if (cbId) instance.removeEventCallback(cbId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicrosoftClick = async () => {
    localStorage.setItem('azure_redirect_in_progress', 'true');
    try {
      await instance.loginRedirect(loginRequest);
      // Page navigates away here — nothing below this line runs
    } catch (err: any) {
      localStorage.removeItem('azure_redirect_in_progress');
      showToast(err?.message ?? 'Microsoft sign-in failed. Please try again.');
    }
  };

  const handleAzureSubmit = async () => {
    if (!azureEmail.trim()) { showToast('Please enter your contact email address.'); return; }
    if (!azurePending) return;
    setAzureLoading(true);
    try {
      const { user }: LoginResult = await loginWithAzure({
        username: azurePending.username,
        name:     azurePending.name,
        email:    azureEmail.trim(),
      });
      setAzurePending(null);
      onLogin({
        id:          user.id,
        name:        user.username || user.name || user.email,
        email:       user.email,
        role:        toRole(user.role),
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        isOwner:     user.isOwner === true,
        sites:       Array.isArray(user.assignedSites) && user.assignedSites.length > 0
                       ? user.assignedSites
                       : ['all'],
      });
    } catch (err) {
      const apiErr = err as ApiError;
      showToast(apiErr?.message ?? 'Microsoft sign-in failed. Please try again.');
    } finally {
      setAzureLoading(false);
    }
  };

  const handleAzureCancel = () => {
    setAzurePending(null);
    setAzureEmail('');
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.925rem',
      height: '48px',
      gap: '24px',
      opacity: 1,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(180, 195, 215, 1)', borderWidth: '1px' },
      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(43,103,176,0.14)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(43,103,176,1) !important', borderWidth: '1.5px !important' },
      '& input': { padding: '13px 14px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  return (
    <>
    <Box sx={{ display: 'flex', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 100% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(circle 420px at 4% 8%, rgba(43,103,176,0.18), rgba(43,103,176,0) 75%),
            radial-gradient(circle 500px at 8% 98%, rgba(30,58,95,0.16), rgba(30,58,95,0) 75%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, auto, auto, 26px 26px',
          backgroundPosition: 'right center, 0 0, 0 0, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat, repeat',
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: { xs: '20px', sm: '32px', md: '48px' },
          px: { xs: 2, sm: 3 },
        }}
      >
      <Box
        sx={{
          my: 'auto',
          width: { xs: '100%', sm: '440px', md: '452px' },
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2.5, sm: 4, md: 5 },
          bgcolor: '#ffffff',
          borderRadius: '22px',
          border: '1px solid rgba(226,232,240,0.7)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.03), 0 10px 22px -14px rgba(30,58,95,0.12)',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: { xs: 48, sm: 56, md: 80 }, maxWidth: '100%', objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {/* Heading */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.25 }}>
            <Box sx={{ width: 26, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, rgba(43,103,176,1), rgba(27,72,132,1))' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(43,103,176,1)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Fleet &amp; Fuel Intelligence
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Unbounded", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '24px', sm: '26px', md: '33.31px' },
              lineHeight: { xs: '34px', sm: '36px', md: '48px' },
              letterSpacing: 0,
              color: '#0F1D30',
            }}
          >
            Welcome back!
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: 1.5,
              mt: 0.5,
              letterSpacing: 0,
              color: 'rgba(87, 81, 81, 1)',
            }}
          >
            Fuel tracking made transparent, smart, and reliable
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>

          {/* Email */}
          <Typography sx={labelSx}>Email address</Typography>
          <TextField
            fullWidth
            placeholder="Enter your email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            onKeyPress={handleKeyPress}
            disabled={loading}
            size="small"
            sx={{ ...inputSx, mb: 1.5 }}
          />

          {/* Password */}
          <Typography sx={labelSx}>Password</Typography>
          <TextField
            fullWidth
            placeholder="Enter your password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            onKeyPress={handleKeyPress}
            disabled={loading}
            size="small"
            sx={{ ...inputSx, mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Remember me + Forgot password */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#1D4ED8' }, p: '6px' }}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.76rem', sm: '0.82rem' }, color: '#374151', whiteSpace: 'nowrap' }}>
                  Remember for 30 days
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              underline="hover"
              sx={{
                fontSize: { xs: '0.76rem', sm: '0.82rem' },
                fontWeight: 500,
                whiteSpace: 'nowrap',
                // If the row ever has to wrap, sit at the right edge rather than orphaned on the left.
                ml: 'auto',
                color: '#F59E0B',
                '&:hover': { color: '#D97706' },
              }}
            >
              Forgot password
            </Link>
          </Box>

          {/* Login button */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading || loginWait.remaining > 0}
            sx={{
              height: '48px',
              background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
              borderRadius: '11px',
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: 0.3,
              boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              mb: 0,
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': { background: '#93C5FD', boxShadow: 'none' },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
                Sending code...
              </Box>
            ) : loginWait.remaining > 0 ? `Try again in ${loginWait.remaining}s` : 'Login'}
          </Button>

          {/* Google/Microsoft sign-in temporarily disabled — flip this back to `true` to restore */}
          {false && (
          <>
          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
            <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', whiteSpace: 'nowrap' }}>Or</Typography>
            <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
          </Box>

          {/* Social buttons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {GOOGLE_CLIENT_ID ? (
              <GoogleSignInButton
                disabled={loading}
                onSuccess={accessToken => { setLoading(true); handleGoogleSuccess(accessToken); }}
                onError={() => { showToast('Google sign-in was cancelled or failed.'); setLoading(false); }}
              />
            ) : (
              <Button
                variant="outlined"
                disabled
                startIcon={<Box component="img" src={GoogleAsset} alt="Google" sx={{ width: 18, height: 18 }} />}
                sx={{
                  height: '43px', borderColor: '#E5E7EB', color: '#9CA3AF',
                  borderRadius: '10px', textTransform: 'none', fontWeight: 500,
                  fontSize: '0.82rem', backgroundColor: '#fff', boxShadow: 'none',
                }}
              >
                Sign in with Google
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleMicrosoftClick}
              disabled={loading}
              startIcon={<Box component="img" src={AzureAsset} alt="Microsoft" sx={{ width: 18, height: 18 }} />}
              sx={{
                height: '43px',
                borderColor: '#E5E7EB',
                color: '#374151',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.82rem',
                backgroundColor: '#fff',
                boxShadow: 'none',
                '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>
          </>
          )}

          {/* Sign up link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: 1.6,
                letterSpacing: '0%',
                color: '#6B7280',
              }}
            >
              Don't have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/signup')}
                underline="hover"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  letterSpacing: '0%',
                  color: 'rgba(43, 103, 176, 1)',
                  verticalAlign: 'baseline',
                }}
              >
                Create Account
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          display: { xs: 'none', lg: 'block' },
          width: 'min(45vw, calc(100vh * 1576 / 2048))',
          height: '100vh',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
        />
        {/* Continue the left panel's exact texture (color, dot-grid, glow) across the seam,
            masked to fade out — so the boundary reads as one continuous surface, not a cut */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 0% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, 26px 26px',
          backgroundPosition: 'left center, 0 0',
          backgroundRepeat: 'no-repeat, repeat',
          WebkitMaskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          maskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          pointerEvents: 'none',
        }} />
        {/* Deepen the shadows toward the brand navy so the photo reads darker and richer */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(200deg, rgba(15,29,48,0.32) 0%, rgba(15,29,48,0.05) 55%, rgba(15,29,48,0.22) 100%)',
          pointerEvents: 'none',
        }} />
      </Box>

    </Box>

    {/* ── Microsoft contact email dialog ── */}
    <Dialog
      open={!!azurePending}
      onClose={handleAzureCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E5E7EB' } }}
    >
      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box component="img" src={AzureAsset} alt="Microsoft" sx={{ width: 24, height: 24 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                One more step
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mt: 0.25 }}>
                Signed in as <strong>{azurePending?.name}</strong>
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleAzureCancel} sx={{ color: '#9CA3AF' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: '0.82rem', color: '#374151', mb: 2 }}>
          Your Microsoft account may use a corporate address. Please confirm the email address you want to use for notifications and alerts.
        </Typography>

        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>
          Contact email <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="email"
          placeholder="your@email.com"
          value={azureEmail}
          onChange={e => setAzureEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAzureSubmit(); }}
          disabled={azureLoading}
          autoFocus
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(43,103,176,1)' },
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleAzureCancel}
            disabled={azureLoading}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 600,
              borderColor: '#E5E7EB', color: '#6B7280',
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleAzureSubmit}
            disabled={azureLoading}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              bgcolor: 'rgba(43,103,176,1)', boxShadow: 'none',
              '&:hover': { bgcolor: 'rgba(33,85,150,1)', boxShadow: 'none' },
            }}
          >
            {azureLoading
              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                  Signing in…
                </Box>
              : 'Continue'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!mfaPending}
      onClose={handleMfaCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E5E7EB', m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: '100%' }, maxWidth: { xs: 400, sm: 444 } } }}
    >
      <DialogContent sx={{ pt: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, px: { xs: 2.25, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
              Enter your login code
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#6B7280', mt: 0.25 }}>
              We emailed a code to <strong style={{ overflowWrap: 'anywhere' }}>{mfaPending?.email}</strong>
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleMfaCancel} sx={{ color: '#9CA3AF', flexShrink: 0, mt: -0.5, mr: -0.75 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>
          Login code <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter code"
          value={mfaCode}
          onChange={e => setMfaCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleVerifyCode(); }}
          disabled={mfaLoading}
          autoFocus
          inputProps={{ maxLength: 10, inputMode: 'numeric', autoComplete: 'one-time-code' }}
          sx={{
            mb: 1,
            '& input': { fontSize: '1rem', letterSpacing: '0.08em' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              '& fieldset': { borderColor: '#E5E7EB' },
              '&:hover fieldset': { borderColor: '#D1D5DB' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(43,103,176,1)' },
            },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <Typography
            component="button"
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading || resendWait.remaining > 0}
            sx={{
              p: 0, border: 0, bgcolor: 'transparent', fontFamily: 'inherit',
              fontSize: '0.78rem', fontWeight: 600,
              color: resendWait.remaining > 0 ? '#9CA3AF' : 'rgba(43,103,176,1)',
              cursor: resendLoading || resendWait.remaining > 0 ? 'default' : 'pointer',
              '&:hover': { textDecoration: resendLoading || resendWait.remaining > 0 ? 'none' : 'underline' },
            }}
          >
            {resendLoading ? 'Sending new code…'
              : resendWait.remaining > 0 ? `Resend code in ${resendWait.remaining}s` : 'Resend code'}
          </Typography>
          {attemptsLeft != null && (
            <Typography sx={{ fontSize: '0.74rem', color: attemptsLeft <= 1 ? '#B91C1C' : '#6B7280' }}>
              {attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} left
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.25 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleMfaCancel}
            disabled={mfaLoading}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 600,
              minHeight: 44, whiteSpace: 'nowrap',
              flex: { xs: '0 0 auto', sm: '0 1 36%' },
              borderColor: '#E5E7EB', color: '#6B7280',
              '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyCode}
            disabled={mfaLoading || loginWait.remaining > 0}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              minHeight: 44, whiteSpace: 'nowrap',
              flex: { xs: '0 0 auto', sm: '1 1 auto' },
              bgcolor: 'rgba(43,103,176,1)', boxShadow: 'none',
              '&:hover': { bgcolor: 'rgba(33,85,150,1)', boxShadow: 'none' },
            }}
          >
            {mfaLoading
              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                  Verifying…
                </Box>
              : 'Verify & Sign In'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>

    <Snackbar
      open={toast.open}
      autoHideDuration={toast.severity === 'success' ? 3000 : 6000}
      onClose={closeToast}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        onClose={closeToast}
        severity={toast.severity}
        variant="filled"
        sx={{ width: '100%', borderRadius: '10px', fontFamily: '"Montserrat", sans-serif', fontSize: '0.85rem' }}
      >
        {toast.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default LoginPage;
